import { getLayerRecommendation } from './layerLogic';

const BASE_URL = 'https://api.open-meteo.com/v1/forecast';

export const getLayerRecommendations = async ({ latitude, longitude, forecastHours = 0 }) => {
  try {
    console.log('🌡️ Racing basic weather APIs for instant data...');
    
    // Create abort controllers for racing APIs
    const primaryController = new AbortController();
    const fallbackController = new AbortController();
    
    const url = `${BASE_URL}?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,apparent_temperature,precipitation,wind_speed_10m&hourly=temperature_2m,apparent_temperature,precipitation,wind_speed_10m&past_days=2&models=metno_seamless`;

    // Primary API call (Open-Meteo with full model)
    const primaryApiCall = fetch(url, { signal: primaryController.signal })
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          return { source: 'primary', data };
        }
        throw new Error(`Primary basic weather API failed: ${response.status}`);
      });

    // Fallback API call (simplified Open-Meteo) - starts after 4 seconds
    const fallbackApiCall = new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          console.log('🌡️ Starting fallback basic weather API after 4s delay...');
          
          // Simplified API call with fewer parameters
          const simpleUrl = `${BASE_URL}?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,apparent_temperature,precipitation,wind_speed_10m`;
          
          const response = await fetch(simpleUrl, { signal: fallbackController.signal });
          if (response.ok) {
            const data = await response.json();
            
            // Simulate hourly data from current data for fallback
            if (!data.hourly) {
              data.hourly = {
                temperature_2m: new Array(72).fill(data.current?.temperature_2m || 15),
                apparent_temperature: new Array(72).fill(data.current?.apparent_temperature || 15),
                precipitation: new Array(72).fill(data.current?.precipitation || 0),
                wind_speed_10m: new Array(72).fill(data.current?.wind_speed_10m || 5)
              };
            }
            
            resolve({ source: 'fallback', data });
          } else {
            throw new Error(`Fallback basic weather API failed: ${response.status}`);
          }
        } catch (error) {
          reject(error);
        }
      }, 4000); // 4 second delay for basic weather
    });

    let data;
    try {
      // Race both APIs
      const result = await Promise.race([
        primaryApiCall.catch(err => {
          console.warn('🌡️ Primary basic weather API failed:', err.message);
          throw err;
        }),
        fallbackApiCall.catch(err => {
          console.warn('🌡️ Fallback basic weather API failed:', err.message);
          throw err;
        })
      ]);
      
      // Cancel the other request
      if (result.source === 'primary') {
        console.log('🌡️ Primary basic weather API won the race!');
        fallbackController.abort();
      } else {
        console.log('🌡️ Fallback basic weather API won the race!');
        primaryController.abort();
      }
      
      data = result.data;
      
    } catch (error) {
      // Both APIs failed - clean up and throw
      console.warn('🌡️ Both basic weather APIs failed:', error);
      primaryController.abort();
      fallbackController.abort();
      throw error;
    }
    
    const now = new Date();
    const currentHourIndex = now.getHours() + (24 * 2);
    const targetIndex = forecastHours > 0 
      ? currentHourIndex + forecastHours 
      : forecastHours === -24 
        ? currentHourIndex - 24 
        : currentHourIndex;

    // Use apparent temperature for recommendations
    const apparentTemperature = forecastHours === 0 
      ? data?.current?.apparent_temperature 
      : data?.hourly?.apparent_temperature?.[targetIndex];
    
    // Get display temperature (actual temp at selected time)
    const displayTemperature = forecastHours === 0 
      ? data?.current?.temperature_2m 
      : data?.hourly?.temperature_2m?.[targetIndex];
    
    const precipitation = forecastHours === 0 
      ? data?.current?.precipitation 
      : data?.hourly?.precipitation?.[targetIndex] || 0;
    
    const windSpeed = forecastHours === 0 
      ? data?.current?.wind_speed_10m 
      : data?.hourly?.wind_speed_10m?.[targetIndex] || 0;

    // Calculate daily high/low from hourly actual temperatures
    const calculateDailyHighLow = () => {
      if (!data?.hourly?.temperature_2m) return { high: displayTemperature, low: displayTemperature };
      
      // Determine which day's data to use
      const today = new Date();
      const targetDate = new Date(today.getTime() + (forecastHours * 60 * 60 * 1000));
      
      // Get 24-hour period for the target date (simplified - using rough 24-hour window)
      const startIndex = Math.max(0, targetIndex - (targetIndex % 24));
      const endIndex = Math.min(data.hourly.temperature_2m.length - 1, startIndex + 23);
      
      const dayTemps = data.hourly.temperature_2m.slice(startIndex, endIndex + 1);
      
      return {
        high: Math.max(...dayTemps),
        low: Math.min(...dayTemps)
      };
    };

    const { high: dailyHigh, low: dailyLow } = calculateDailyHighLow();

    // Get recommendation using shared logic with background colors for UI
    const recommendation = getLayerRecommendation(apparentTemperature, precipitation, windSpeed, true);

    return {
      recommendation,
      temperature: {
        display: displayTemperature,      // Actual temp at selected time for big display
        apparent: apparentTemperature,    // Apparent temp for layer calculations
        dailyHigh: dailyHigh,            // Proper daily high
        dailyLow: dailyLow               // Proper daily low
      },
      conditions: {
        precipitation,
        windSpeed
      }
    };
  } catch (error) {
    console.error('Error racing basic weather APIs:', error);
    throw error;
  }
};